<template>
  <Transition name="slide-fade">
    <aside 
      v-if="visible"
      class="song-comments-sidebar flex flex-col bg-white dark:bg-slate-800 border-l border-gray-200 dark:border-slate-700 transition-all duration-300 select-none overflow-hidden"
      :class="['fixed right-0 top-0 h-full w-80 z-[100] shadow-2xl']"
    >
      <!-- Header -->
      <div class="sidebar-header flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50/80 dark:bg-slate-900/60">
        <div class="flex items-center gap-2 min-w-0">
          <ChatBubbleLeftEllipsisIcon class="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
          <h3 class="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">
            {{ title || $t('editor.notes') || 'Comentários' }}
            <span class="ml-1 text-xs px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-medium">
              {{ comments.length }}
            </span>
          </h3>
        </div>
        
        <div class="flex items-center gap-1 flex-shrink-0">
          <!-- Close Button -->
          <button 
            @click="closeSidebar" 
            class="p-1.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-slate-700 transition-colors"
            :title="$t('editor.close') || 'Fechar'"
          >
            <XMarkIcon class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Comments List -->
      <div ref="listContainerRef" class="flex-1 overflow-y-auto p-3 space-y-3 no-scrollbar">
        <div v-if="comments.length === 0" class="flex flex-col items-center justify-center h-48 text-center px-4">
          <ChatBubbleLeftIcon class="w-10 h-10 text-gray-300 dark:text-slate-600 mb-2" />
          <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">
            {{ $t('editor.noCommentsFound') }}
          </p>
        </div>

        <div 
          v-for="comment in comments" 
          :key="comment.id"
          :ref="(el) => setCommentCardRef(el, comment.id)"
          @click="selectComment(comment.id)"
          class="comment-card group relative p-3 rounded-lg border transition-all duration-200 cursor-pointer text-left"
          :class="[
            activeCommentId === comment.id
              ? 'bg-indigo-50/90 dark:bg-indigo-950/50 border-indigo-400 dark:border-indigo-500 shadow-md ring-2 ring-indigo-400/30 dark:ring-indigo-500/30'
              : 'bg-white dark:bg-slate-800/90 border-gray-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 shadow-sm'
          ]"
        >
          <!-- Selected text snippet -->
          <div v-if="comment.selectedText" class="text-xs text-gray-500 dark:text-gray-400 italic mb-2 border-l-2 border-indigo-500 pl-2 line-clamp-2">
            "{{ comment.selectedText }}"
          </div>

          <!-- Comment text content -->
          <div class="text-xs text-gray-800 dark:text-gray-200 font-medium leading-relaxed break-words">
            {{ comment.commentText }}
          </div>

          <!-- Action buttons (Delete) -->
          <div v-if="editable" class="mt-2.5 pt-2 border-t border-gray-100 dark:border-slate-700/60 flex items-center justify-end">
            <button 
              @click.stop="$emit('delete-comment', comment.id)"
              class="text-[11px] font-semibold text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 px-2 py-1 rounded transition-colors"
            >
              {{ $t('editor.deleteNote') || 'Excluir' }}
            </button>
          </div>
        </div>
      </div>
    </aside>
  </Transition>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, nextTick } from 'vue';
import { 
  ChatBubbleLeftEllipsisIcon, 
  ChatBubbleLeftIcon, 
  XMarkIcon 
} from '@heroicons/vue/24/outline';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  pinned: {
    type: Boolean,
    default: false
  },
  comments: {
    type: Array,
    default: () => []
  },
  editable: {
    type: Boolean,
    default: true
  },
  activeCommentId: {
    type: [String, Number],
    default: null
  },
  title: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:visible', 'update:pinned', 'select-comment', 'delete-comment', 'close']);

const cardRefs = ref({});
const listContainerRef = ref(null);

const setCommentCardRef = (el, id) => {
  if (el) {
    cardRefs.value[id] = el;
  }
};

const closeSidebar = () => {
  emit('update:visible', false);
  emit('close');
};

const selectComment = (id) => {
  emit('select-comment', id);
};

watch(() => props.activeCommentId, (newId) => {
  if (newId && cardRefs.value[newId]) {
    nextTick(() => {
      const cardEl = cardRefs.value[newId];
      if (cardEl && cardEl.scrollIntoView) {
        cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }
});
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
